import React, { useState } from 'react';
import { FiDownload, FiUpload, FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import { downloadBackup, restoreBackup } from '../services/api';
import { toast } from 'react-toastify';
import './BackupRestore.css';

function BackupRestore({ isOpen, onClose, onRestoreComplete }) {
  const [restoring, setRestoring] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [clearExisting, setClearExisting] = useState(false);

  if (!isOpen) return null;

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await downloadBackup();
      toast.success('Backup downloaded successfully!');
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error('Failed to download backup: ' + (error.response?.data?.error || error.message));
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
        toast.error('Please select a valid JSON backup file');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile) {
      toast.error('Please select a backup file');
      return;
    }

    if (clearExisting) {
      const confirmed = window.confirm(
        'WARNING: This will DELETE all existing data before restoring the backup. This action cannot be undone!\n\nAre you sure you want to continue?'
      );
      if (!confirmed) {
        return;
      }
    }

    try {
      setRestoring(true);
      const result = await restoreBackup(selectedFile, clearExisting);
      toast.success(
        `Backup restored successfully! Restored ${result.stats.projects} projects, ${result.stats.blocks} blocks, ${result.stats.users} users.`
      );
      setSelectedFile(null);
      setClearExisting(false);
      // Reset file input
      const fileInput = document.getElementById('backup-file-input');
      if (fileInput) {
        fileInput.value = '';
      }
      if (onRestoreComplete) {
        onRestoreComplete();
      }
      onClose();
    } catch (error) {
      console.error('Error restoring backup:', error);
      toast.error('Failed to restore backup: ' + (error.response?.data?.error || error.message));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="backup-restore-modal-overlay" onClick={onClose}>
      <div className="backup-restore-modal" onClick={(e) => e.stopPropagation()}>
        <div className="backup-restore-header">
          <h2>Backup & Restore</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="backup-restore-content">
          {/* Download Backup Section */}
          <div className="backup-section">
            <div className="section-header">
              <FiDownload className="section-icon" />
              <h3>Download Backup</h3>
            </div>
            <p className="section-description">
              Download a complete backup of all projects, blocks, users, and status details as a JSON file.
            </p>
            <button
              className="backup-download-btn"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? 'Downloading...' : 'Download Backup'}
            </button>
          </div>

          {/* Restore Backup Section */}
          <div className="restore-section">
            <div className="section-header">
              <FiUpload className="section-icon" />
              <h3>Restore Backup</h3>
            </div>
            <p className="section-description">
              Restore data from a previously downloaded backup file.
            </p>

            <div className="file-input-wrapper">
              <input
                id="backup-file-input"
                type="file"
                accept=".json,application/json"
                onChange={handleFileSelect}
                className="file-input"
              />
              <label htmlFor="backup-file-input" className="file-input-label">
                {selectedFile ? selectedFile.name : 'Select Backup File'}
              </label>
            </div>

            {selectedFile && (
              <div className="file-info">
                <FiCheckCircle className="file-info-icon" />
                <span>{selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)</span>
              </div>
            )}

            <div className="restore-options">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={clearExisting}
                  onChange={(e) => setClearExisting(e.target.checked)}
                />
                <span>Clear existing data before restore</span>
              </label>
              {clearExisting && (
                <div className="warning-box">
                  <FiAlertTriangle className="warning-icon" />
                  <div>
                    <strong>Warning:</strong> This will permanently delete all existing projects, blocks, users, and status details before restoring the backup. This action cannot be undone!
                  </div>
                </div>
              )}
            </div>

            <button
              className="backup-restore-btn"
              onClick={handleRestore}
              disabled={!selectedFile || restoring}
            >
              {restoring ? 'Restoring...' : 'Restore Backup'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BackupRestore;

